import React from 'react';
import { ArrowRight, ArrowUpRight, Package } from 'lucide-react';
import { motion } from 'framer-motion';
interface PathwaysSectionProps {
  onOpenSampleModal?: () => void;
}
export function PathwaysSection({
  onOpenSampleModal
}: PathwaysSectionProps) {
  const handleSampleClick = (e: React.MouseEvent) => {
    if (onOpenSampleModal) {
      e.preventDefault();
      onOpenSampleModal();
    }
  };
  return <section className="py-24 bg-white relative overflow-hidden" data-id="element-417">
      {/* Background decoration element */}
      <div className="absolute right-0 top-1/2 w-[500px] h-[500px] bg-slate-50 rounded-tl-[200px] rounded-bl-[200px] -translate-y-1/2 translate-x-1/2 opacity-60" data-id="element-418"></div>

      <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative" data-id="element-419">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-420">
          {/* Large Card - Standard Products */}
          <motion.a href="/products" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="group relative bg-[#FAFBFC] rounded-[32px] p-10 lg:p-12 border border-slate-100 overflow-hidden flex flex-col justify-between min-h-[480px] cursor-pointer hover:border-[#0052CC]/30 hover:shadow-xl transition-all duration-300" data-id="element-421">
            <div className="relative z-10" data-id="element-422">
              <div className="flex items-center justify-between mb-6" data-id="element-423">
                <span className="text-xs font-bold text-[#0052CC] uppercase tracking-widest" data-id="element-424">
                  01
                </span>
                <div className="h-10 w-10 bg-[#0052CC] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300" data-id="element-425">
                  <ArrowUpRight className="h-5 w-5 text-white" data-id="element-426" />
                </div>
              </div>
              <h3 className="text-3xl font-bold text-[#0A1628] mb-4" data-id="element-427">
                Standard Products
              </h3>
              <p className="text-slate-500 max-w-md text-lg leading-relaxed" data-id="element-428">
                Browse our extensive range of stock labels, tags, and safety
                signs. Ready for immediate dispatch across Europe.
              </p>
            </div>

            <div className="relative z-10 mt-8" data-id="element-429">
              <span className="inline-flex items-center px-6 py-3 bg-[#0A1628] text-white font-bold rounded-full group-hover:bg-[#0052CC] transition-colors duration-300" data-id="element-430">
                Shop Online
                <ArrowRight className="ml-2 h-4 w-4" data-id="element-431" />
              </span>
            </div>

            {/* Abstract Background Decoration */}
            <div className="absolute right-0 bottom-0 w-64 h-64 bg-slate-100 rounded-tl-[100px] opacity-50 group-hover:scale-110 transition-transform duration-700" data-id="element-432"></div>
          </motion.a>

          {/* Right Column - Stacked Cards */}
          <div className="flex flex-col gap-6" data-id="element-433">
            {/* Bespoke Solutions */}
            <motion.a href="/bespoke" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="group flex-1 bg-[#0A1628] rounded-[32px] p-8 lg:p-10 border border-slate-700 relative overflow-hidden flex flex-col justify-center cursor-pointer hover:border-[#0052CC]/50 transition-colors duration-300" data-id="element-434">
              <div className="relative z-10" data-id="element-435">
                <div className="flex items-center justify-between mb-5" data-id="element-436">
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-widest" data-id="element-437">
                    02
                  </span>
                  <div className="h-10 w-10 bg-white/10 rounded-full flex items-center justify-center group-hover:bg-[#0052CC] transition-colors duration-300" data-id="element-438">
                    <ArrowUpRight className="h-5 w-5 text-white" data-id="element-439" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3" data-id="element-440">
                  Bespoke Solutions
                </h3>
                <p className="text-slate-400 max-w-sm leading-relaxed" data-id="element-441">
                  Custom engineered identification for specific environments and
                  compliance needs.
                </p>
              </div>
              <div className="absolute inset-0 bg-gradient-to-r from-[#0052CC]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" data-id="element-442"></div>
            </motion.a>

            {/* Request Samples - Enhanced with button */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="group flex-1 bg-gradient-to-br from-[#0052CC] to-[#0040A0] rounded-[32px] p-8 lg:p-10 relative overflow-hidden flex flex-col justify-center" data-id="element-443">
              {/* Background decoration */}
              <div className="absolute right-0 bottom-0 w-48 h-48 bg-white/10 rounded-tl-[80px] opacity-50" data-id="element-444"></div>

              <div className="relative z-10" data-id="element-445">
                <div className="flex items-center justify-between mb-5" data-id="element-446">
                  <div className="flex items-center gap-3" data-id="element-447">
                    <div className="h-10 w-10 bg-white/20 rounded-xl flex items-center justify-center" data-id="element-448">
                      <Package className="h-5 w-5 text-white" data-id="element-449" />
                    </div>
                    <span className="text-xs font-bold text-white/60 uppercase tracking-widest" data-id="element-450">
                      03
                    </span>
                  </div>
                  <div className="h-10 w-10 bg-white/20 rounded-full flex items-center justify-center" data-id="element-451">
                    <ArrowUpRight className="h-5 w-5 text-white" data-id="element-452" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3" data-id="element-453">
                  Request Samples
                </h3>
                <p className="text-white/70 max-w-sm mb-6 leading-relaxed" data-id="element-454">
                  Test our materials in your environment. Free sample packs
                  available for qualifying businesses.
                </p>
                <button onClick={handleSampleClick} className="inline-flex items-center px-6 py-3 bg-white text-[#0052CC] font-bold rounded-full hover:bg-slate-100 transition-colors shadow-lg" data-id="element-455">
                  Request a sample pack
                  <ArrowRight className="ml-2 h-4 w-4" data-id="element-456" />
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}